import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Target } from 'lucide-react';
import { useProjectsList } from '../../../../../../contexts/ProjectsContext';
import {
  downloadSalesDailyReportPdf,
  getSalesActivityTargets,
  getSalesDailyDrilldown,
  getSalesDailyReport
} from '../../../../services/api/implementation/impl';
import DownloadPdfButton from '../../../Engineer/reports/DownloadPdfButton';
import useReportPdf from '../shared/useReportPdf';
import ReportShell, { CardGrid, FilterSelect, ResultBanner } from '../shared/ReportShell';
import DateRangeControl from '../shared/DateRangeControl';
import DrilldownDrawer from '../shared/DrilldownDrawer';
import MetricCard from '../shared/MetricCard';
import SectionTitle from '../shared/SectionTitle';
import useDailyReport from '../shared/useDailyReport';
import { DEFAULT_RANGE, rangeLabel, rangeParams } from '../shared/dateRange';
import { fmtPct, targetText } from '../shared/format';
import SalespersonTable from './SalespersonTable';

/** What each card opens, and the one-line definition shown above the list. */
const DRILLDOWN = {
  qualified: { title: 'Qualified pre-leads', subtitle: 'A lead was created from the pre-lead.' },
  disqualified: { title: 'Disqualified pre-leads', subtitle: 'Marked unqualified.' },
  pendingCalls: { title: 'Pending calls', subtitle: 'Waiting for their first call.' },
  overdueCalls: {
    title: 'Overdue calls',
    subtitle: 'Waiting for a first call, SLA red. Target: 0.'
  },
  noResponse: { title: 'No response', subtitle: 'Last call was Not Connected.' },
  followUpsScheduled: { title: 'Follow-ups scheduled', subtitle: 'Follow-ups due in this period.' },
  followUpsCompleted: { title: 'Follow-ups completed', subtitle: 'Due in this period and done.' },
  siteVisitsScheduled: { title: 'Site visits scheduled', subtitle: 'Visits due in this period.' },
  siteVisitsTaken: { title: 'Site visits taken', subtitle: 'Visits marked as Site Visited.' },
  closedWon: { title: 'Closed Won', subtitle: 'Deals closed in this period.' }
};

function describeRecord(item) {
  const when = item.date ? moment(item.date).format('D MMM, h:mm A') : '';
  return {
    key: `${item.type}-${item.id}`,
    title: item.name,
    subtitle: [item.phone, item.assignee || 'Unassigned', item.status].filter(Boolean).join(' · '),
    meta: when,
    href: item.leadId ? `/builder/sales/${item.leadId}` : null
  };
}

function resultItems(team, perUser) {
  const pre = team.preLeads;
  const leads = team.leads;
  const items = [];

  const worst = [...perUser]
    .filter((u) => u.userId && u.preLeads.overdueCalls > 0)
    .sort((a, b) => b.preLeads.overdueCalls - a.preLeads.overdueCalls)[0];
  items.push(
    pre.overdueCalls > 0
      ? {
          key: 'overdue',
          tone: 'bad',
          text: (
            <>
              <strong>{pre.overdueCalls} overdue calls</strong> (target 0).
              {worst ? ` ${worst.name} has ${worst.preLeads.overdueCalls}.` : ''}
            </>
          )
        }
      : {
          key: 'overdue',
          tone: 'good',
          text: (
            <>
              <strong>No overdue calls.</strong> Every pre-lead is within its SLA.
            </>
          )
        }
  );

  const fuTarget = team.targets?.followUps;
  items.push({
    key: 'followups',
    tone: fuTarget && fuTarget.variance < 0 ? 'warn' : 'good',
    text: (
      <>
        <strong>{fmtPct(leads.followUpCompletionPct)} of follow-ups done</strong>:{' '}
        {leads.followUpsCompleted} of {leads.followUpsScheduled}
        {fuTarget ? ` (${targetText(fuTarget).toLowerCase()}).` : '.'}
      </>
    )
  });

  items.push({
    key: 'won',
    tone: leads.closedWon > 0 ? 'good' : 'warn',
    text: (
      <>
        <strong>
          {leads.closedWon} {leads.closedWon === 1 ? 'customer' : 'customers'} won
        </strong>
        ; {leads.siteVisitsTaken} of {leads.siteVisitsScheduled} site visits attended (
        {fmtPct(leads.siteVisitAttendancePct)}).
      </>
    )
  });
  return items;
}

const SalesDailyReport = () => {
  const { projectsForDropdown = [] } = useProjectsList();
  const [range, setRange] = useState(DEFAULT_RANGE);
  // Opens on the first project, like the operations reports; "All projects"
  // stays available. Projects may arrive after the first render.
  const [projectId, setProjectId] = useState(() => String(projectsForDropdown[0]?.id ?? ''));
  const projectPicked = useRef(false);
  useEffect(() => {
    if (!projectPicked.current && !projectId && projectsForDropdown.length) {
      setProjectId(String(projectsForDropdown[0].id));
    }
  }, [projectsForDropdown, projectId]);
  const pickProject = (id) => {
    projectPicked.current = true;
    setProjectId(id);
  };
  const [salesUserId, setSalesUserId] = useState('');
  const [drill, setDrill] = useState(null);

  const params = useMemo(
    () => ({ ...rangeParams(range), project_id: projectId, sales_user_id: salesUserId }),
    [range, projectId, salesUserId]
  );
  const { data, loading, error, refetch } = useDailyReport(getSalesDailyReport, params);
  // Only for canEdit: whether to offer "Set daily targets".
  const roster = useDailyReport(getSalesActivityTargets, { project_id: projectId });

  const { downloadPdf, isDownloading } = useReportPdf(downloadSalesDailyReportPdf);
  const openDrill = (metric) => setDrill(metric);
  const fetchPage = useCallback(
    (page) => getSalesDailyDrilldown({ ...params, metric: drill, ...page }),
    [params, drill]
  );

  const team = data?.team;
  const pre = team?.preLeads;
  const leads = team?.leads;
  const targets = team?.targets;
  const label = data ? rangeLabel(data.range) : '';

  return (
    <ReportShell
      title="Sales Daily Report"
      subtitle={
        label ? `Report date: ${label}` : 'Pre-leads, follow-ups, site visits and conversions'
      }
      loading={loading}
      hasData={!!data}
      error={error}
      onRetry={refetch}
      actions={
        <>
          <DownloadPdfButton
            onClick={() => downloadPdf(params)}
            isDownloading={isDownloading}
            disabled={!data || loading || !!error}
          />
          {roster.data?.canEdit && (
            <Link
              to="/builder/reports/sales-daily/targets"
              className="flex items-center gap-2 min-h-[44px] px-4 bg-[#1F4B55] text-white rounded-md text-sm font-semibold hover:bg-[#163a42] no-underline"
            >
              <Target size={18} aria-hidden="true" />
              Set daily targets
            </Link>
          )}
        </>
      }
      filters={
        <>
          <FilterSelect label="Project" value={projectId} onChange={pickProject} minWidth={220}>
            <option value="">All projects</option>
            {projectsForDropdown.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </FilterSelect>
          <DateRangeControl value={range} onChange={setRange} allowLifetime maxDays={366} />
        </>
      }
    >
      {team && (
        <>
          <ResultBanner
            title={`Result for ${label}`}
            items={resultItems(team, data.perUser || [])}
          />

          <section aria-labelledby="pre-leads" className="flex flex-col gap-4">
            <SectionTitle
              id="pre-leads"
              note={
                data.preLeadsAllProjects
                  ? `${pre.total} pre-leads in this period · all projects (pre-leads are not linked to a project)`
                  : `${pre.total} pre-leads in this period · % of all pre-leads`
              }
            >
              A. Pre-Leads
            </SectionTitle>
            <CardGrid min={250}>
              <MetricCard
                label="Qualified vs pre-leads"
                headline={fmtPct(pre.qualifiedPct)}
                unit="qualified"
                detail={`${pre.qualified} of ${pre.total} pre-leads`}
                percent={pre.qualifiedPct}
                onClick={() => openDrill('qualified')}
              />
              <MetricCard
                label="Disqualified vs pre-leads"
                headline={fmtPct(pre.disqualifiedPct)}
                unit="disqualified"
                detail={`${pre.disqualified} of ${pre.total} pre-leads`}
                percent={pre.disqualifiedPct}
                onClick={() => openDrill('disqualified')}
              />
              <MetricCard
                label="Pending calls vs pre-leads"
                headline={fmtPct(pre.pendingPct)}
                unit="awaiting first call"
                detail={`${pre.pendingCalls} of ${pre.total} pre-leads`}
                badge={`${pre.overdueCalls} overdue (red SLA) · target 0`}
                alert={pre.overdueCalls > 0}
                onClick={() => openDrill(pre.overdueCalls > 0 ? 'overdueCalls' : 'pendingCalls')}
              />
              <MetricCard
                label="No response vs pre-leads"
                headline={fmtPct(pre.noResponsePct)}
                unit="called, not connected"
                detail={`${pre.noResponse} of ${pre.total} pre-leads`}
                percent={pre.noResponsePct}
                onClick={() => openDrill('noResponse')}
              />
            </CardGrid>
          </section>

          <section aria-labelledby="leads" className="flex flex-col gap-4">
            <SectionTitle id="leads" tone="teal" note="Targets shown where they are set">
              B. Leads
            </SectionTitle>
            <CardGrid min={220}>
              <MetricCard
                label="Follow-ups scheduled"
                headline={leads.followUpsScheduled}
                unit="due"
                detail="Total count"
                onClick={() => openDrill('followUpsScheduled')}
              />
              <MetricCard
                label="Follow-ups completed"
                headline={fmtPct(leads.followUpCompletionPct)}
                unit="completion"
                detail={`${leads.followUpsCompleted} of ${leads.followUpsScheduled} scheduled`}
                percent={leads.followUpCompletionPct}
                target={targets?.followUps}
                onClick={() => openDrill('followUpsCompleted')}
              />
              <MetricCard
                label="Site visits scheduled"
                headline={leads.siteVisitsScheduled}
                unit="visits"
                detail="Total count only"
                target={targets?.siteVisits}
                onClick={() => openDrill('siteVisitsScheduled')}
              />
              <MetricCard
                label="Site visits taken"
                headline={fmtPct(leads.siteVisitAttendancePct)}
                unit="attendance"
                detail={`${leads.siteVisitsTaken} of ${leads.siteVisitsScheduled} scheduled`}
                percent={leads.siteVisitAttendancePct}
                onClick={() => openDrill('siteVisitsTaken')}
              />
              <MetricCard
                label="Closed Won → Customer"
                headline={leads.closedWon}
                unit={leads.closedWon === 1 ? 'customer won' : 'customers won'}
                detail="Closed deals in this period"
                target={targets?.conversions}
                onClick={() => openDrill('closedWon')}
              />
            </CardGrid>
          </section>

          {!salesUserId && (data.perUser || []).length > 0 && (
            <section aria-labelledby="people" className="flex flex-col gap-4">
              <SectionTitle id="people" note="Select a name to see only that person">
                By Salesperson
              </SectionTitle>
              <SalespersonTable
                rows={data.perUser}
                team={team}
                onSelect={(userId) => setSalesUserId(String(userId))}
              />
            </section>
          )}

          {salesUserId && (
            <button
              type="button"
              onClick={() => setSalesUserId('')}
              className="self-start text-sm font-semibold text-[#1F4B55] underline min-h-[44px]"
            >
              Back to the whole team
            </button>
          )}
        </>
      )}

      <DrilldownDrawer
        open={!!drill}
        title={drill ? DRILLDOWN[drill].title : ''}
        subtitle={drill ? `${DRILLDOWN[drill].subtitle} ${label}.` : ''}
        fetchPage={fetchPage}
        describe={describeRecord}
        onClose={() => setDrill(null)}
      />
    </ReportShell>
  );
};

export default SalesDailyReport;
